import { ApiRequest } from '@zeal/zeal-lib';
import kebabCase from 'lodash/kebabCase';
import { LoadPathOption } from 'i18next-http-backend';

export function plural(word: string) {
	const plural: Record<string, string> = {
		'(quiz)$': '$1zes',
		'^(ox)$': '$1en',
		'([m|l])ouse$': '$1ice',
		'(matr|vert|ind)ix|ex$': '$1ices',
		'(x|ch|ss|sh)$': '$1es',
		'([^aeiouy]|qu)y$': '$1ies',
		'(hive)$': '$1s',
		'(?:([^f])fe|([lr])f)$': '$1$2ves',
		'(shea|lea|loa|thie)f$': '$1ves',
		sis$: 'ses',
		'(tomat|potat|ech|her|vet)o$': '$1oes',
		'(bu)s$': '$1ses',
		'(alias)$': '$1es',
		'(octop)us$': '$1i',
		'(ax|test)is$': '$1es',
		'(us)$': '$1es',
		'([^s]+)$': '$1s',
	};

	const irregular: Record<string, string> = {
		move: 'moves',
		foot: 'feet',
		goose: 'geese',
		sex: 'sexes',
		child: 'children',
		man: 'men',
		tooth: 'teeth',
		person: 'people',
	};

	const uncountable = [
		'sheep',
		'fish',
		'deer',
		'moose',
		'series',
		'species',
		'money',
		'rice',
		'information',
		'equipment',
		'bison',
		'cod',
		'offspring',
		'pike',
		'salmon',
		'shrimp',
		'swine',
		'trout',
		'aircraft',
		'hovercraft',
		'spacecraft',
		'sugar',
		'tuna',
		'you',
		'wood',
	];
	// save some time in the case that singular and plural are the same
	if (uncountable.indexOf(word.toLowerCase()) >= 0) {
		return word;
	}
	// check for irregular forms
	for (const w in irregular) {
		const pattern = new RegExp(`${w}$`, 'i');
		const replace = irregular[w];
		if (pattern.test(word)) {
			return word.replace(pattern, replace);
		}
	}
	// check for matches using regular expressions
	for (const reg in plural) {
		const pattern = new RegExp(reg, 'i');
		if (pattern.test(word)) {
			return word.replace(pattern, plural[reg]);
		}
	}
	return word;
}

export const getCollectionName = (moduleName: string) => {
	const formatted = kebabCase(plural(moduleName.replace(/\.json$/, '')));

	const prefix = 'web-vendor';
	return `${prefix}-${formatted}`;
};

interface StrapiResponse {
	data: {
		id: string;
		attributes: {
			key: string;
			value: string;
		};
	}[];
	meta: {
		pagination: {
			page: number;
			pageSize: number;
			pageCount: number;
			total: number;
		};
	};
}

const STRAPI_API_KEY = import.meta.env.REACT_APP_STRAPI_TOKEN;
const translationBaseUrl = import.meta.env.REACT_APP_STRAPI_URL ?? '';

export const loadTranslationPath: LoadPathOption = (langs, namespaces) => {
	const collectionName = getCollectionName(namespaces[0]);

	return `${translationBaseUrl}/api/${collectionName}?locale=${langs[0]}`;
};

const createStrapiRequest = (url: string, page: number) => {
	const req = new ApiRequest(url, undefined, {
		hasStringQuery: true,
	});

	req.addHeader('Authorization', `Bearer ${STRAPI_API_KEY}`);
	req.addQuery('pagination[page]', page.toString());
	req.addQuery('pagination[pageSize]', '400');

	return req.GET<StrapiResponse>({
		noAuth: true,
		hasStringQuery: true,
	});
};

export async function fetchTranslation(url: string) {
	// First fetch to get total number of pages
	const initialResponse = await createStrapiRequest(url, 1);
	const totalPages = initialResponse.meta.pagination.pageCount;

	if (totalPages === 1) {
		// If there's only one page, return it directly
		return parseStrapiResponseToJSON(initialResponse);
	}

	// Create an array of promises for remaining pages (excluding page 1)
	const pagePromises = Array.from(
		{ length: totalPages - 1 },
		(_, index) => createStrapiRequest(url, index + 2) // Start from page 2
	);

	// Fetch remaining pages in parallel
	const responses = await Promise.all(pagePromises);

	// Combine initial page data with the rest of the pages
	const allData = [
		...initialResponse.data,
		...responses.flatMap((response) => response.data),
	];

	return parseStrapiResponseToJSON({
		data: allData,
		meta: {
			pagination: {
				page: 1,
				pageSize: allData.length,
				pageCount: 1,
				total: allData.length,
			},
		},
	});
}

function parseStrapiResponseToJSON(
	response: StrapiResponse
): Record<string, string> {
	return response.data.reduce((acc, item) => {
		acc[item.attributes.key] = item.attributes.value;
		return acc;
	}, {} as Record<string, string>);
}
